import {DateTime} from "luxon";
import {Logger} from "@/util/logger";
import {SimulationJob, SimulationStatusType} from "@/models/sim/simulation.job";
import {basename} from "@/util/util";

/**
 *
 * @class RemoteSimJob
 *
 * @brief property holder for details around a remote simulation job, being run on
 * one of the simulation engine nodes.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2024
 *
 */
export class RemoteSimJob {

  /** unique job id (within this remote simulator) */
  public id: number                                   = 0;

  /** alias for id */
  public jid: number                                  = 0;

  /** human-readable modification time */
  public ago: string                                  = '';

  /** scenario id (deprecated) */
  public scenario_id: number                         = 0;

  /** simulator process id */
  public pid: number                                 = 0;

  /** the scenario's unique id (scenario property) */
  public client_id: number                           = 0;

  /** the job's status */
  public status: SimulationStatusType                = SimulationStatusType.READY;

  /** the most recent known error message */
  public lasterror: string                           = '';

  /** simulator author (scenario property) */
  public author: string                              = '';

  /** simulation full name (scenario property) */
  public name: string                               = '';

  /** when the job expires */
  public expiry: DateTime                            = DateTime.now();

  /** when it was pulled from the queue */
  public dequeue_at: DateTime                        = DateTime.now();

  /** when added to the queue */
  public created_at: DateTime                        = DateTime.now();

  /** last modification */
  public updated_at: DateTime                        = DateTime.now();

  /** meta data */
  public extra: any                                  = {};

  /** is breakable */
  public is_breakable: boolean                       = false

  /** is interruptible */
  public is_interrupted: boolean                    = false

  /** is recurring */
  public is_recur: boolean                           = false

  /** finished ok flag */
  public is_successful: boolean                      = false

  /** the scenario or "plant and traffic" definitions */
  public importfile: string                          = '';

  /** alias for importfile */
  public input: string                               = '';

  /** the simulator log file */
  public log: string                                 = '';

  /** the simulated train movement data */
  public output: string                              = '';

  /** the simulation errors file */
  public simcmd: string                              = '';

  /** the simulation errors file */
  public simerrors: string                           = '';

  /** the KPI errors files */
  public kpi: string                                 = '';

  /** the KPI errors files */
  public kpix: string                                = '';

  /** when doing verbose tracing, this is the trace file */
  public tracefile: string                           = '';

  /** the user logging file, typically a MongoDB URL */
  public userlog: string                             = '';

  /** the options for verbose tracing */
  public trace: string                               = '';

  /** sections of the simulator queue */
  public queue_class: string                         = '';

  /** priority of simulation job */
  public priority: string                            = '';

  /** simulator hook output */
  public response: string                            = '';

  /** schedule for recurring simulations */
  public schedule: string                            = '';

  /** the summary progress number */
  public progress: string                            = '';

  /** how long the simulating took (seconds) */
  public runtime: number                             = 0;

  /** total timing including queuing */
  public totaltime: number                           = 0;

  /** how long in the queue */
  public waittime: number                            = 0;

  /** detailed statistics */
  public last_stats: any                             = {};

  /* simulator configuration options */
  public options: any                                = {};

  /** accumulated progress messages */
  public progress_out: any                           = {};

  public version: string                             = '';
  public compile_time: string                        = '';
  public compile_date: string                        = '';
  public passenger_reaction_time: number             = 0;
  public freight_reaction_time: number               = 0;

  /** cache of the artifact mappings for this job */
  private artifactMap: {kind: string, url: string}[] = [];


  /** standard constructor */
  public constructor(init?: Partial<RemoteSimJob>) {

    /* up convert */

    if (typeof init['expiry'] != 'undefined') {

      init['expiry'] = DateTime.fromISO((init as any)['expiry'].replace(' ', 'T'), {zone: 'UTC'}).toLocal();
    }

    if (typeof init['dequeue_at'] != 'undefined') {

      init['dequeue_at'] = DateTime.fromISO((init as any)['dequeue_at'].replace(' ', 'T'), {zone: 'UTC'}).toLocal();
    }

    if (typeof init['created_at'] != 'undefined') {

      init['created_at'] = DateTime.fromISO((init as any)['created_at'].replace(' ', 'T'), {zone: 'UTC'}).toLocal();
    }

    if (typeof init['updated_at'] != 'undefined') {

      init['updated_at'] = DateTime.fromISO((init as any)['updated_at'].replace(' ', 'T'), {zone: 'UTC'}).toLocal();
    }

    if (typeof init['extra'] != 'undefined') {

      try {

        if (typeof init['extra'] !== 'object' && init['extra'] !== null) {

          init['extra'] = JSON.parse(init['extra']);
        }

      } catch (error) {

        Logger.error(`[RemoteSimJob] bad job 'extra' formatting (can't parse JSON): `, init['extra']);

        init['extra'] = {};
      }
    }

    if (typeof init['extra']?.job_detail == 'object') {

      let details = init['extra'].job_detail;

      if (typeof details['Compile Time'] != 'undefined') {

        init['compile_time'] = details['Compile Time'];
      }

      if (typeof details['Compile Date'] != 'undefined') {

        init['compile_date'] = details['Compile Date'];
      }

      if (typeof details['Version'] != 'undefined') {

        init['version'] = details['Version'];
      }

      if (typeof details['reaction_time']?.passenger_mins != 'undefined') {

        init['passenger_reaction_time'] = details['reaction_time']?.passenger_mins * 60;
      }

      if (typeof details['reaction_time']?.passenger_reaction_time != 'undefined') {

        /* the actual line simulator outputs the reaction time in hours */

        init['passenger_reaction_time'] = details['reaction_time']?.passenger_reaction_time * 3600;
      }

      if (typeof details['reaction_time']?.non_passenger_mins != 'undefined') {

        init['freight_reaction_time'] = details['reaction_time']?.non_passenger_mins * 60;
      }

      if (typeof details['reaction_time']?.freight_reaction_time != 'undefined') {

        /* the actual line simulator outputs the reaction time in hours */

        init['freight_reaction_time'] = details['reaction_time']?.freight_reaction_time * 3600;
      }
    }

    if (typeof init['last_stats'] != 'undefined') {

      try {

        if (typeof init['last_stats'] !== 'object' && init['last_stats'] !== null) {

          /* it's likely a string that we need to JSON decode ... */

          init['last_stats'] = JSON.parse(init['last_stats']);
        }

      } catch (error) {

        Logger.error(`[RemoteSimJob] bad job 'last_stats' formatting (can't parse JSON): `, init['last_stats']);

        init['last_stats'] = {};
      }
    }

    if (typeof init['options'] != 'undefined') {

      try {

        if (typeof init['options'] !== 'object' && init['options'] !== null) {

          init['options'] = JSON.parse(init['options']);
        }

      } catch (error) {

        Logger.error(`[RemoteSimJob] bad job 'options' formatting (can't parse JSON): `, init['options']);

        init['options'] = {};
      }

      if (typeof init['options']['name'] != 'undefined') {

        init['name'] = init['options']['name'];
      }

      if (typeof init['options']['client_id'] != 'undefined') {

        init['client_id'] = init['options']['client_id'];
      }

      if (typeof init['author']['name'] != 'undefined') {

        init['author'] = init['options']['author'];
      }
    }

    if (typeof init['progress_out'] != 'undefined') {

      try {

        if (typeof init['progress_out'] !== 'object' && init['progress_out'] !== null) {

          init['progress_out'] = JSON.parse(init['progress_out']);
        }

      } catch (error) {

        Logger.error(`[RemoteSimJob] bad job 'progress_out' formatting (can't parse JSON): `, init['progress_out']);

        init['progress_out'] = {};

      }

    }

    if (typeof init['input'] != 'undefined') {

      init['input'] = init['importfile'];
    }

    if (typeof init['jid'] != 'undefined') {

      init['jid'] = init['id'];
    }

    if (typeof init['status'] != 'undefined') {

      init['status'] = SimulationJob.statusType(<any>init['status'] as string);
    }

    if (typeof init['extra']['artifact-kpi-download'] != 'undefined') {

      let stub: string = basename(init['extra']['artifact-kpi-download']);

      init['kpi'] = `/rest/download/kpi/${stub}?id=${init['jid']}`;
    }

    if (typeof init['extra']['artifact-kpix-download'] != 'undefined') {

      let stub: string = basename(init['extra']['artifact-kpix-download']);

      init['kpix'] = `/rest/download/kpix/${stub}?id=${init['jid']}`;
    }

    if (typeof init['extra']['artifact-sim-log-download'] != 'undefined') {

      let stub: string = basename(init['extra']['artifact-sim-log-download']);

      init['log'] = `/rest/download/log/${stub}?id=${init['jid']}`;
    }

    if (typeof init['extra']['artifact-sim-cmd-download'] != 'undefined') {

      let stub: string = basename(init['extra']['artifact-sim-cmd-download']);

      init['simcmd'] = `/rest/download/cmd/${stub}?id=${init['jid']}`;
    }

    if (typeof init['extra']['artifact-sim-errors-download'] != 'undefined') {

      let stub: string = basename(init['extra']['artifact-sim-errors-download']);

      init['simerrors'] = `/rest/download/errors/${stub}?id=${init['jid']}`;
    }

    /* do any overrides */

    (<any>Object).assign(this, init);
  }

  /** convert to simplified JSON form */
  toJSON(): any {

    return {
      id:                      this.id,
      ago:                     this.ago,
      jid:                     this.jid,
      scenario_id:             this.scenario_id,
      pid:                     this.pid,
      client_id:               this.client_id,
      status:                  SimulationJob.statusString(this.status),
      lasterror:               this.lasterror,
      author:                  this.author,
      name:                    this.name,
      expiry:                  this.expiry.toString(),
      dequeue_at:              this.dequeue_at.toString(),
      created_at:              this.created_at.toString(),
      updated_at:              this.updated_at.toString(),
      extra:                   JSON.stringify(this.extra),
      is_breakable:            this.is_breakable,
      is_interrupted:          this.is_interrupted,
      is_recur:                this.is_recur,
      is_successful:           this.is_successful,
      importfile:              this.importfile,
      input:                   this.input,
      log:                     this.log,
      output:                  this.output,
      simerrors:               this.simerrors,
      simcmd:                  this.simcmd,
      kpi:                     this.kpi,
      kpix:                    this.kpix,
      tracefile:               this.tracefile,
      userlog:                 this.userlog,
      trace:                   this.trace,
      queue_class:             this.queue_class,
      priority:                this.priority,
      response:                this.response,
      schedule:                this.schedule,
      progress:                this.progress,
      runtime:                 this.runtime,
      totaltime:               this.totaltime,
      waittime:                this.waittime,
      last_stats:              JSON.stringify(this.last_stats),
      options:                 JSON.stringify(this.options),
      progress_out:            JSON.stringify(this.progress_out),
      version:                 this.version,
      compile_time:            this.compile_time,
      compile_date:            this.compile_date,
      passenger_reaction_time: this.passenger_reaction_time,
      freight_reaction_time:   this.freight_reaction_time
    };
  }


  /** fetch the list of artifacts, and leave the URL blank if we don't have that artifact (yet?) */
  public artifacts(): {kind: string, url: string}[] {

    if (this.artifactMap.length == 0) {

      this.artifactMap = [
        {
          kind: 'kpix',
          url:  this.extra['artifact-kpix-download'] ?? '',
        },
        {
          kind: 'movement',
          url:  this.extra['artifact-movement-download'] ?? '',
        },

        {
          kind: 'scenario',
          url:  this.extra['artifact-scenario-download'] ?? '',
        },

        {
          kind: 'cmd',
          url:  this.extra['artifact-sim-cmd-download'] ?? '',
        },

        {
          kind: 'errors',
          url:  this.extra['artifact-sim-errors-download'] ?? '',
        },

        {
          kind: 'log',
          url:  this.extra['artifact-sim-log-download'] ?? '',
        }
      ];

    }

    return this.artifactMap;
  }
}
